import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectPostalCode } from "../../store/reducers/formInfoSlice";
import { selectSelectedId as selectSelectedModelId } from "../../store/reducers/modelChoiceSlice";
import { selectDimensionsBySelectedId } from "../../store/reducers/dimensionsSlice";
import { selectShedOptionsState } from "../../store/reducers/shedOptionsSlice";
import { selectColorChoices } from "../../store/reducers/colorChoiceSlice";
import { selectSelectedId as isSelectedPanelColorId } from "../../store/reducers/panelColorChoiceSlice";
import { selectSelectedId as selectSelectedPanelId } from "../../store/reducers/panelChoiceSlice";
import { selectSelectedSquares } from "../../store/reducers/panelPlacementSlice";
import {
  countSelectedSquaresByModel,
  modelToPanelPrefix,
} from "../Summary/PDFGenerationUtils/choiceTranslationUtils/countingUtils";
import { calculateGaragePrice, calculateGatePrice } from "./garagePriceUtils";
import { calculateRoomPrice } from "./roomPriceUtils";
import { getPriceForDimensions } from "./dimensionPriceUtils";
import {
  getAntykondensatPrice,
  calculateAntykondensatCost,
} from "./antykondensatPriceUtils";
import { getPhotovoltaicsReinforcementPrice } from "./photovoltaicsPriceUtils";
import { getPanelPrice, calculatePanelsPrice } from "./panelsPriceUtils";

const PriceCalculator = () => {
  const [dimensionsData, setDimensionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://marciniakogrodzenia.pl/wp-content/uploads/2024/07/compiled_data2.json"
        );
        setDimensionsData(response.data);
        setLoading(false);
      } catch (err) {
        setError(`Error fetching data: ${err.message}`);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const postalCode = useSelector(selectPostalCode);
  const selectedModelId = useSelector(selectSelectedModelId);
  const dimensions = useSelector((state) =>
    selectDimensionsBySelectedId(state, selectedModelId)
  );
  const shedOptions = useSelector(selectShedOptionsState);
  const colorChoices = useSelector(selectColorChoices);
  const selectedPanelColorId = useSelector(isSelectedPanelColorId);
  const selectedPanelId = useSelector(selectSelectedPanelId);
  const selectedSquares = useSelector(selectSelectedSquares);
  const garageOptions = shedOptions.selectedGarageOption;

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const calculateNumberOfPoles = (modelId, length, selectedSquares) => {
    let numberOfPoles;
    switch (modelId) {
      case "1.a":
      case "1.b":
        numberOfPoles = length >= 700 ? 11 : 9;
        break;
      case "1.c":
        numberOfPoles = length >= 700 ? 22 : 18;
        break;
      case "1.d":
      case "1.f":
        numberOfPoles = length >= 700 ? 14 : 12;
        break;
      case "1.e":
        numberOfPoles = length >= 700 ? 15 : 13;
        break;
      default:
        numberOfPoles = 0;
    }

    if (modelId === "1.a" && !selectedSquares["6.1.d"]) {
      numberOfPoles -= 1;
    } else if (
      modelId === "1.b" &&
      !selectedSquares["6.1.d"] &&
      !selectedSquares["6.1.e"]
    ) {
      numberOfPoles -= 1;
    } else if (modelId === "1.c") {
      if (!selectedSquares["6.1.d"]) numberOfPoles -= 1;
      if (!selectedSquares["6.1.e"]) numberOfPoles -= 1;
    } else if (
      modelId === "1.e" &&
      !selectedSquares["6.1.d"] &&
      !selectedSquares["6.1.e"]
    ) {
      numberOfPoles -= 1;
    }

    return numberOfPoles;
  };

  const calculatePolePrice = (dimensions, numberOfPoles) => {
    const pole2mm = dimensionsData.find(
      (d) => d["Rozmiar (szer. x dł.)"] === "Słup 100x100x2mm \ndo 3m wysokości"
    )["Cena Netto (zł) zaciagana do konfiguratora"];
    const pole3mm = dimensionsData.find(
      (d) =>
        d["Rozmiar (szer. x dł.)"] === "Słup 100x100x3mm \npowyżej 3m wysokości"
    )["Cena Netto (zł) zaciagana do konfiguratora"];
    const singlePolePrice = dimensionsData.find(
      (d) =>
        d["Rozmiar (szer. x dł.)"] === "Słup pojedynczy \ndla wysokości 2,7m"
    )["Cena Netto (zł) zaciagana do konfiguratora"];

    let polePrice = 0;

    if (dimensions.height < 270) {
      polePrice = singlePolePrice;
    } else if (dimensions.height >= 270 && dimensions.height < 300) {
      polePrice = ((dimensions.height - 270) / 100) * numberOfPoles * pole2mm;
    } else if (dimensions.height >= 300) {
      polePrice =
        2.35 * (pole3mm - pole2mm) * numberOfPoles +
        ((dimensions.height - 270) / 100) * numberOfPoles * pole3mm;
    }

    return polePrice;
  };

  let finalPrice = 0;

  const numberOfPoles = calculateNumberOfPoles(
    selectedModelId,
    dimensions.length,
    selectedSquares
  );

  if (dimensions && dimensions.length && dimensions.width) {
    const dimensionPrice = getPriceForDimensions(
      dimensionsData,
      dimensions.length,
      dimensions.width
    );

    console.log(`kalkulacja kosztu na podstawie wymiarów: ${dimensionPrice}`);
    finalPrice += dimensionPrice;
  }

  const panelPrice = getPanelPrice(
    selectedPanelId,
    selectedPanelColorId,
    dimensionsData
  );
  

  console.log(`koszt pojedyńczego panelu: ${panelPrice}`);


  const panelsPrice = calculatePanelsPrice(
    selectedModelId,
    dimensions,
    selectedSquares,
    shedOptions,
    garageOptions,
    panelPrice,
    modelToPanelPrefix,
    countSelectedSquaresByModel
  );
  console.log(`koszt łączny paneli: ${panelsPrice}`);
  finalPrice += panelsPrice;

  const photovoltaicReinforcementPrice =
    getPhotovoltaicsReinforcementPrice(dimensionsData);

  if (shedOptions.isPhotovoltaicsReinforcementChecked) {
    console.log(`fotowoltaika koszt: ${photovoltaicReinforcementPrice}`);
    finalPrice += photovoltaicReinforcementPrice;
  }

  const antykondensatPrice = getAntykondensatPrice(dimensionsData);
  const antykondensatCost = calculateAntykondensatCost(
    dimensions,
    antykondensatPrice
  );
  console.log(`koszt antykondensatu: ${antykondensatCost}`);
  finalPrice += antykondensatCost;

  if (shedOptions.isRoomChecked) {
    const roomPrice = calculateRoomPrice(
      dimensionsData,
      dimensions,
      selectedModelId
    );
    console.log(`obliczony koszt pomieszczenia gospodarczego: ${roomPrice}`);
    finalPrice += roomPrice;
  }

  if (shedOptions.isGarageChecked) {
    const garagePrice = calculateGaragePrice(
      dimensions,
      garageOptions,
      dimensionsData
    );
    console.log(`szerokosc ${dimensions.width}`);
    console.log(`obliczony koszt garazu: ${garagePrice}`);
  
    finalPrice += garagePrice;
  
    const gatePrice = calculateGatePrice(
      dimensions,
      garageOptions,
      dimensionsData
    );
    console.log(`obliczony koszt bramy garazowej: ${gatePrice}`);
    finalPrice += gatePrice;
  }

  const facadeColor = colorChoices.facadeColor;
  const facadeColorIds = ["4.3.b", "4.3.c", "4.3.d", "4.3.e", "4.3.f"];
  if (facadeColorIds.includes(facadeColor)) {
    const maskownicaStandard = dimensionsData.find(
      (d) => d["Rozmiar (szer. x dł.)"] === "Maskownica Standard"
    );
    const maskownicaPrice = maskownicaStandard
      ? maskownicaStandard["Cena Netto (zł) zaciagana do konfiguratora"]
      : 0;
    finalPrice += maskownicaPrice;
    console.log(`koszt maskownicy: ${maskownicaPrice}`)
  }

  const polePrice = calculatePolePrice(dimensions, numberOfPoles);
  console.log(`ilosc słupów: ${numberOfPoles}`)
  console.log(`koszt słupów: ${polePrice}`)
  finalPrice += polePrice;

  return (
    <div>
      <div>Estimated Price: {finalPrice}</div>
      <div>Ilośc słupów: {numberOfPoles}</div>
    </div>
  );
};

export default PriceCalculator;
